import { Client as TwilioConversationsClient } from '@twilio/conversations'
import {
  PushNotifications,
  Token,
  ActionPerformed,
  PushNotificationSchema,
} from '@capacitor/push-notifications'

import { isAndroid, isCapacitor, isIOS } from 'src/utils/capacitor'

export type PushPlatform = 'apn' | 'fcm' | 'web'

export const detectPushPlatform = (): PushPlatform => {
  if (isIOS()) return 'apn'
  if (isAndroid()) return 'fcm'
  return 'web'
}

interface NotificationHandlers {
  onTap?: (data: Record<string, unknown>) => void
  onForeground?: (notification: PushNotificationSchema) => void
}

let listenersRegistered = false

const registerListeners = (
  twilioClient: TwilioConversationsClient,
  handlers: NotificationHandlers
) => {
  if (listenersRegistered) return
  listenersRegistered = true

  void PushNotifications.addListener('registration', (token: Token) => {
    const platform = detectPushPlatform()
    if (platform === 'web') return

    void twilioClient
      .setPushRegistrationId(platform, token.value)
      .catch((err) => console.error('Twilio setPushRegistrationId failed:', err))
  })

  void PushNotifications.addListener('registrationError', (err) => {
    console.error('PushNotifications registration error:', err)
  })

  void PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      handlers.onForeground?.(notification)
    }
  )

  void PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      const data = (action.notification.data ?? {}) as Record<string, unknown>
      handlers.onTap?.(data)
    }
  )
}

const ensureAndroidChannel = async () => {
  if (!isAndroid()) return
  await PushNotifications.createChannel({
    id: 'connectup_default',
    name: 'ConnectUp',
    description: 'ConnectUp messages and notifications',
    importance: 4, // IMPORTANCE_HIGH — heads-up notifications
    visibility: 1, // VISIBILITY_PUBLIC — full content on lockscreen
    vibration: true,
    lights: true,
  })
}

export const initNativePush = async (
  twilioClient: TwilioConversationsClient,
  handlers: NotificationHandlers = {}
) => {
  if (!isCapacitor()) return

  await ensureAndroidChannel()
  registerListeners(twilioClient, handlers)

  const status = await PushNotifications.checkPermissions()
  let granted = status.receive === 'granted'

  if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
    const requested = await PushNotifications.requestPermissions()
    granted = requested.receive === 'granted'
  }

  if (!granted) {
    console.warn('Push notification permission not granted')
    return
  }

  await PushNotifications.register()
}
